import { Box, Typography } from '@material-ui/core'
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import { Fragment } from 'react'

import { DateRange } from '@/services/create-pulverization'
import { LayerValue, Pulverization } from '@/types/weed-diagnosis'
import {
  formatPulverizationDateRange,
  transformUnderscoreInSpaceAndUppercaseFirstLetter,
} from '@/utils/format-strings'

import { useStyles } from './styles'

interface Layer
  extends Omit<Pulverization, 'layer' | 'area' | 'composed_name_layers'> {
  area: LayerValue
  name: string
}

interface Group {
  name: string
  area: LayerValue
  layers: Layer[]
}

export interface SummaryTableProps {
  summary?: {
    title: string
    subtitle: string
    head: LayerValue
    groups: Group[]
    intersect?: boolean
    pulverization_date?: DateRange
  }
  withInfoGroup?: boolean
}

export function SummaryTable({ summary, withInfoGroup }: SummaryTableProps) {
  const classes = useStyles()

  return (
    <div className={classes.root}>
      <header>
        <Typography variant='h3'>{summary?.title}</Typography>
      </header>

      <table className={classes.infestTable}>
        <thead>
          <tr>
            <th>&bull;</th>
            <th>{summary?.subtitle}</th>
            <th>{summary?.head.ha.toFixed(2)} ha</th>
            <th>({summary?.head.pc.toFixed(2)}%)</th>
          </tr>
        </thead>
        <tbody>
          {summary?.groups.map((group) => (
            <Fragment key={group.name}>
              <tr>
                <th>&bull;</th>
                <th>
                  {transformUnderscoreInSpaceAndUppercaseFirstLetter(
                    group.name,
                  )}
                  {summary?.intersect && (
                    <Box className={classes.intersectInfo}>
                      Intersecção das camadas aplicado
                    </Box>
                  )}
                </th>
                <th>{group.area.ha.toFixed(2)} ha</th>
                <th>({group.area.pc.toFixed(2)}%)</th>
              </tr>

              {group.layers.map((layer) => (
                <tr key={layer.name} data-with-info-group={withInfoGroup}>
                  <td>
                    <Box
                      bgcolor={layer.colorPalette}
                      minWidth={16}
                      minHeight={16}
                      maxWidth={16}
                      maxHeight={16}
                      borderRadius={4}
                      marginTop='1px'
                      marginX='auto'
                    />
                  </td>
                  <td>
                    <span>
                      {transformUnderscoreInSpaceAndUppercaseFirstLetter(
                        layer.name,
                      )}
                    </span>

                    {withInfoGroup && (
                      <ul>
                        <li aria-label='Buffer'>
                          <span>&bull;</span>
                          <div>
                            {layer.buffer
                              ? `Buffer: ${layer.buffer}m`
                              : 'Buffer: Não selecionado'}
                          </div>
                        </li>
                        <li aria-label='Tratamento'>
                          <span>&bull;</span>
                          {layer.herbicide_treatment?.treatment_name
                            ? `Tratamento: ${layer.herbicide_treatment?.treatment_name}`
                            : 'Tratamento: Não selecionado'}
                        </li>
                        <li aria-label='Display'>
                          <span>&bull;</span>
                          {layer.display?.name
                            ? `Equipamento: ${layer.display.name}`
                            : 'Equipamento: Não selecionado'}
                        </li>
                        <li aria-label='Vazão'>
                          <span>&bull;</span>
                          {layer.display?.dose
                            ? `Vazão: ${layer.display.dose} L/ha`
                            : 'Vazão: Não selecionado'}
                        </li>

                        {layer.display && layer.display.shared !== null && (
                          <li aria-label='Integração'>
                            {layer.display?.shared ? (
                              <CheckCircleOutlinedIcon />
                            ) : (
                              <ReportProblemOutlinedIcon
                                className={classes.errorIcon}
                              />
                            )}
                            <div>
                              Operations Center:{' '}
                              {layer.display?.shared
                                ? 'Arquivos enviados'
                                : 'Arquivos não enviados'}
                            </div>
                          </li>
                        )}
                        {layer.display?.organization?.name && (
                          <li aria-label='Organização'>
                            <span>&bull;</span>
                            <div>
                              Organização: {layer.display?.organization?.name}
                            </div>
                          </li>
                        )}
                        {layer.display?.is_drone && (
                          <li aria-label='Drones' className={classes.infoDrone}>
                            <ReportProblemOutlinedIcon
                              className={classes.errorIcon}
                            />
                            <div>
                              Atenção: Verifique obstáculos antes de iniciar o
                              voo.
                            </div>
                          </li>
                        )}
                      </ul>
                    )}
                  </td>
                  <td>{layer.area.ha.toFixed(2)} ha</td>
                  <td>({layer.area.pc.toFixed(2)}%)</td>
                </tr>
              ))}
            </Fragment>
          ))}
          {withInfoGroup && summary?.pulverization_date && (
            <tr>
              <td />
              <td>
                <ul>
                  <li aria-label='Data'>
                    <span>&bull;</span>
                    Data prevista para pulverização:
                    <br />
                    {formatPulverizationDateRange(summary?.pulverization_date)}
                  </li>
                </ul>
              </td>
              <td />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
